import { IconCalendar, IconList } from '@tabler/icons-react';
import { Button, Card, CardBody, CustomInput, Input } from 'reactstrap';
import Select from 'react-select';
import {
  VISIT_STATUS,
  VISIT_STATUS_NAMES,
  VisitPreSaleState,
  VISIT_PRE_SALE_STATUS_NAMES,
  ADVISORY_STATUS_NAMES,
} from '../../../constants/visitsConstants';
import useSdrList from '../../../hooks/useSdrList';
import { Option } from '../../../models/formModel';
import { activeTab } from '../types/dashboard.types';
import { AdvisoryLeadStatus } from '../models/dashboardModels';

interface DashboardFilterBarProps {
  activeTab: activeTab;
  isRestricted: boolean;
  startDate: string;
  endDate: string;
  onDateRangeChange: (startDate: string, endDate: string) => void;
  status: string;
  onStatusChange: (status: string) => void;
  showOnlyAssigned?: boolean;
  onShowOnlyAssignedChange?: (checked: boolean) => void;
  selectedSdrId?: string;
  onSdrChange?: (sdrId: string) => void;
  onClearFilters: () => void;
  hideAssignmentControl?: boolean;
  // Solo aplica con activeTab === 'advisory'.
  deliveryTime?: string;
  onDeliveryTimeChange?: (deliveryTime: string) => void;
  // Periodo que el backend aplica por defecto (yyyy-mm-dd): se muestra dentro
  // del propio filtro de fechas mientras el usuario no elija otro rango.
  defaultPeriod?: { start: string; end: string };
  // Selector de vista Lista / Calendario (sólo si se pasa).
  viewMode?: DashboardViewMode;
  onViewModeChange?: (mode: DashboardViewMode) => void;
}

export type DashboardViewMode = 'list' | 'calendar';

const VIEW_MODE_OPTIONS = [
  { value: 'list', label: 'Lista', Icon: IconList },
  { value: 'calendar', label: 'Calendario', Icon: IconCalendar },
] as const;

const TAB_SDR_ROLE: Record<
  activeTab,
  'SDR_RENT' | 'SDR_SALES' | 'SDR_PRESALES' | null
> = {
  rent: 'SDR_RENT',
  secondary: 'SDR_SALES',
  'pre-sale': 'SDR_PRESALES',
  // Advisory mezcla SDR_SALES/SDR_PRESALES según delivery_time (ver
  // auto-asignación en user-service) — sin un único rol para el selector,
  // el filtro de SDR queda oculto en este tab (hideAssignmentControl).
  advisory: null,
};

const ADVISORY_STATUS_VALUES: AdvisoryLeadStatus[] = [
  'pending_approval',
  'scheduled',
  'rescheduled',
  'cancelled',
  'finished',
];

const DELIVERY_TIME_OPTIONS: Option[] = [
  { value: 'inmediata', label: 'Inmediata' },
  { value: 'planos', label: 'Planos' },
];

const getStatusOptions = (tab: activeTab): Option[] => {
  if (tab === 'pre-sale') {
    return Object.values(VisitPreSaleState).map((value) => ({
      value,
      label: VISIT_PRE_SALE_STATUS_NAMES[value],
    }));
  }
  if (tab === 'advisory') {
    return ADVISORY_STATUS_VALUES.map((value) => ({
      value,
      label: ADVISORY_STATUS_NAMES[value],
    }));
  }
  return Object.values(VISIT_STATUS).map((value) => ({
    value,
    label: VISIT_STATUS_NAMES[value],
  }));
};

const DashboardFilterBar = ({
  activeTab: tab,
  isRestricted,
  startDate,
  endDate,
  onDateRangeChange,
  status,
  onStatusChange,
  showOnlyAssigned,
  onShowOnlyAssignedChange,
  selectedSdrId,
  onSdrChange,
  onClearFilters,
  hideAssignmentControl,
  deliveryTime,
  onDeliveryTimeChange,
  viewMode,
  onViewModeChange,
  defaultPeriod,
}: DashboardFilterBarProps) => {
  const shownStart = startDate || defaultPeriod?.start || '';
  const shownEnd = endDate || defaultPeriod?.end || '';
  const { sdrOptions, isLoadingSdrs } = useSdrList(TAB_SDR_ROLE[tab]);
  const statusOptions = getStatusOptions(tab);

  return (
    <Card className="mb-4 dv2-filters">
      <CardBody>
        <div className="dv2-filters-row">
          <div className="dv2-filter-pill">
            <IconCalendar size={16} />
            <span className="dv2-filter-label">Rango</span>
            <Input
              type="date"
              aria-label="Fecha inicio"
              value={shownStart}
              onChange={(e) => onDateRangeChange(e.target.value, shownEnd)}
            />
            <span className="dv2-filter-sep">al</span>
            <Input
              type="date"
              aria-label="Fecha fin"
              value={shownEnd}
              onChange={(e) => onDateRangeChange(shownStart, e.target.value)}
            />
          </div>

          <div className="dv2-filter-pill">
            <span className="dv2-filter-label">Estado</span>
            <Select
              options={statusOptions}
              isClearable
              placeholder="Todos"
              value={statusOptions.find((o) => o.value === status) ?? null}
              onChange={(option) => onStatusChange(option?.value ?? '')}
            />
          </div>

          {tab === 'advisory' && (
            <div className="dv2-filter-pill">
              <span className="dv2-filter-label">Entrega</span>
              <Select
                options={DELIVERY_TIME_OPTIONS}
                isClearable
                placeholder="Todas"
                value={
                  DELIVERY_TIME_OPTIONS.find((o) => o.value === deliveryTime) ??
                  null
                }
                onChange={(option) =>
                  onDeliveryTimeChange?.(option?.value ?? '')
                }
              />
            </div>
          )}

          {!hideAssignmentControl &&
            (isRestricted ? (
              <div className="dv2-filter-pill">
                <CustomInput
                  type="checkbox"
                  id="showOnlyAssignedCheckbox"
                  label="Ver lo asignado"
                  checked={showOnlyAssigned}
                  onChange={(e) => onShowOnlyAssignedChange?.(e.target.checked)}
                />
              </div>
            ) : (
              <div className="dv2-filter-pill">
                <span className="dv2-filter-label">Asignado a</span>
                <Select
                  options={sdrOptions}
                  isClearable
                  isLoading={isLoadingSdrs}
                  placeholder="Todos"
                  value={
                    sdrOptions.find((o) => o.value === selectedSdrId) ?? null
                  }
                  onChange={(option) => onSdrChange?.(option?.value ?? '')}
                />
              </div>
            ))}

          <div className="dv2-filters-actions">
            {viewMode && onViewModeChange && (
              <div className="dv2-view-toggle" role="group">
                {VIEW_MODE_OPTIONS.map(({ value, label, Icon }) => (
                  <button
                    key={value}
                    type="button"
                    className={viewMode === value ? 'is-active' : ''}
                    aria-pressed={viewMode === value}
                    onClick={() => onViewModeChange(value)}
                  >
                    <Icon size={16} />
                    {label}
                  </button>
                ))}
              </div>
            )}
            <Button color="secondary" outline onClick={onClearFilters}>
              Limpiar filtros
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

export default DashboardFilterBar;
